import { useState, useEffect, useCallback } from 'react';
import { useMedplum, useSubscription } from '@medplum/react';
import { DiagnosticReport } from '@medplum/fhirtypes';
import { searchDiagnosticReportsForOrder } from '../utils/fhir-helpers';

// -----------------------------------------------------------------------------
export interface UseDiagnosticReportsProps {
  serviceRequestId?: string;
}

// -----------------------------------------------------------------------------
export const useDiagnosticReportsList = ({
  serviceRequestId,
}: UseDiagnosticReportsProps) => {
  const medplum = useMedplum();
  const [reports, setReports] = useState<DiagnosticReport[]>([]);
  const [loading, setLoading] = useState(false);

  // ---------------------------------------------------------------------------
  const loadReports = useCallback(async () => {
    if (!serviceRequestId) {
      setReports([]);
      return;
    }

    setLoading(true);
    try {
      const diagnosticReports = await searchDiagnosticReportsForOrder(
        medplum,
        serviceRequestId
      );
      setReports(diagnosticReports);
    } catch (error) {
      console.error('Error loading diagnostic reports:', error);
      setReports([]);
    } finally {
      setLoading(false);
    }
  }, [medplum, serviceRequestId]);

  // ---------------------------------------------------------------------------
  useEffect(() => {
    loadReports();
  }, [loadReports]);
  // ---------------------------------------------------------------------------
  // Subscribe to DiagnosticReport changes for this order
  useSubscription(
    serviceRequestId
      ? `DiagnosticReport?based-on=ServiceRequest/${serviceRequestId}`
      : '',
    (bundle) => {
      const updatedReport = bundle.entry?.find(
        (dr) => dr.resource?.resourceType === 'DiagnosticReport'
      )?.resource as DiagnosticReport | undefined;

      if (!updatedReport) {
        return;
      }

      setReports((prev) => {
        const exists = prev.find((dr) => dr.id === updatedReport.id);

        if (exists) {
          // Update existing report
          return prev.map((dr) =>
            dr.id === updatedReport.id ? updatedReport : dr
          );
        } else {
          // Add new report and sort by issued date
          const updated = [updatedReport, ...prev];

          return updated.sort((a, b) => {
            const dateA = new Date(a.issued || '').getTime();
            const dateB = new Date(b.issued || '').getTime();
            return dateB - dateA; // Descending
          });
        }
      });
    }
  );

  // ---------------------------------------------------------------------------
  return {
    reports,
    loading,
    loadReports,
    hasReports: reports.length > 0,
    latestReport: reports[0],
  };
};
